import type {
  CreateUserAccountIdInput,
  DataResponse,
  IdCategoryInput,
  ListMyAccountIdsQuery,
  ListUserAccountIdsQuery,
  PaginatedResponse,
  UpdateUserAccountIdInput,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Response types ──────────────────────────────────────────────────────────

export interface IdCategory {
  id: string;
  name: string;
  icon_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountIdUser {
  id: string;
  name: string;
  employee_code: string | null;
}

export interface UserAccountId {
  id: string;
  category_id: string;
  account_identifier: string;
  created_at: string;
  updated_at: string;
  category_name: string;
  category_icon_path: string | null;
  users: AccountIdUser[];
}

export interface MyAccountId {
  id: string;
  category_id: string;
  category_name: string;
  category_icon_path: string | null;
  account_identifier: string;
  updated_at: string;
}

// ── API client ──────────────────────────────────────────────────────────────

export const idManagementService = {
  // Categories
  async listCategories(): Promise<IdCategory[]> {
    const res = await api.get<DataResponse<IdCategory[]>>('/id-management/categories');
    return res.data.data;
  },

  async createCategory(input: IdCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/id-management/categories', input);
    return res.data.data;
  },

  async updateCategory(id: string, input: IdCategoryInput): Promise<void> {
    await api.patch(`/id-management/categories/${id}`, input);
  },

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/id-management/categories/${id}`);
  },

  async uploadCategoryIcon(id: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('icon', file);
    await api.post(`/id-management/categories/${id}/icon`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  async removeCategoryIcon(id: string): Promise<void> {
    await api.delete(`/id-management/categories/${id}/icon`);
  },

  // Account IDs (admin)
  async listAccounts(query: Partial<ListUserAccountIdsQuery>): Promise<PaginatedResponse<UserAccountId>> {
    const res = await api.get<PaginatedResponse<UserAccountId>>('/id-management/accounts', { params: query });
    return res.data;
  },

  async createAccount(input: CreateUserAccountIdInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/id-management/accounts', input);
    return res.data.data;
  },

  async updateAccount(id: string, input: UpdateUserAccountIdInput): Promise<void> {
    await api.patch(`/id-management/accounts/${id}`, input);
  },

  async deleteAccount(id: string): Promise<void> {
    await api.delete(`/id-management/accounts/${id}`);
  },

  // My IDs (self-service)
  async listMine(query: Partial<ListMyAccountIdsQuery>): Promise<PaginatedResponse<MyAccountId>> {
    const res = await api.get<PaginatedResponse<MyAccountId>>('/id-management/my', { params: query });
    return res.data;
  },
};
